import * as rxjs from 'rxjs';

import * as Elevated from '@pitaman71/omniglot-introspect';
import { BaseMutation } from './BaseMutation';
import { ScalarClient } from '../Clients';
import * as Stores from '../Stores';
import { ScalarStream } from '../Streams';
import { Mutations, Streams } from '..';

export let debugMode = false;

export class Status<ValueType> extends Elevated.Serializable {
    status: Streams.Status = {};

    // implement required methods from Serializable
    static ClassSpec = 'omiglot-live-data.Mutations.OfScalar.Status';
    static Factory = Elevated.factories.concrete<Status<any>>(Status.ClassSpec, () => new Status<any>());
    getFactory = () => Status.Factory;
    getGlobalId(): number|string|null { return null; }
    marshal(visitor: Elevated.Visitor<this>): void {
        visitor.begin(this);
        visitor.verbatim(this, (the: any) => the.status, (the: any, status_) => the.status = status_);
        visitor.end(this);
    }

    toString(): string {
        const globalId = this.getGlobalId();
        return `${this.getFactory()} { }`;
    }
    static from<ValueType>(status_: Streams.Status) {
        const result = new Status<ValueType>();
        result.status = status_;
        return result;
    }
}

export class Revert<ValueType> extends Elevated.Serializable {
    toMutation = new Elevated.Reference<Mutation<ValueType>>();

    // implement required methods from Serializable
    static ClassSpec = 'omiglot-live-data.Mutations.OfScalar.Revert';
    static Factory = Elevated.factories.concrete<Revert<any>>(Revert.ClassSpec, () => new Revert<any>());
    getFactory = () => Revert.Factory;
    getGlobalId(): number|string|null { return null; }
    marshal(visitor: Elevated.Visitor<this>): void {
        visitor.begin(this);
        visitor.reference(Mutation.Factory, 'toMutation');
        visitor.end(this);
    }

    toString(): string {
        const globalId = this.getGlobalId();
        return `${this.getFactory()} ${this.toMutation}`;
    }
    static from<ValueType>(obj: {
        toMutation: Elevated.Reference<Mutation<ValueType>>
    }) {
        const result = new Revert<ValueType>();
        result.toMutation = obj.toMutation
        return result;
    }
}

export class Assign<ValueType> extends Elevated.Serializable {
    value: ValueType|undefined;

    // implement required methods from Serializable
    static Factory = Elevated.factories.concrete('omiglot-live-data.Mutations.OfScalar.Assign', () => new Assign<any>());
    getFactory = () => Assign.Factory;
    getGlobalId(): number|string|null { return null; }
    marshal(visitor: Elevated.Visitor<this>): void {
        visitor.begin(this);
        visitor.primitive(this, 'value');
        visitor.end(this)
    }

    toString(): string {
        const globalId = this.getGlobalId();
        return `${this.getFactory()} { value: ${this.value}}`;
    }    
    
    static from<ValueType>(obj: {
        value: ValueType|undefined
    }) {
        const result = new Assign<ValueType>();
        result.value = obj.value;
        return result;
    }
}

export class Clear<ValueType> extends Elevated.Serializable {
    // implement required methods from Serializable
    static ClassSpec = 'omiglot-live-data.Mutations.OfScalar.Clear';
    static Factory = Elevated.factories.concrete<Clear<any>>(Clear.ClassSpec, () => new Clear<any>());
    getFactory = () => Clear.Factory;
    getGlobalId(): number|string|null { return null; }
    marshal(visitor: Elevated.Visitor<this>): void {
        visitor.begin(this);
        visitor.end(this);
    }

    toString(): string {
        const globalId = this.getGlobalId();
        return `${this.getFactory()} { }`;
    }
    static from<ValueType>(obj: {
    }) {
        const result = new Clear<ValueType>();
        return result;
    }
}

export class Mutation<ValueType> extends BaseMutation {
    id: string;
    status?: Status<ValueType>;
    revert?: Revert<ValueType>;
    assign?: Assign<ValueType>;
    clear?: Clear<ValueType>;
    
    private constructor() { super(); this.id = Elevated.factories.makeObjectId() }

    // implement required methods from Serializable
    static ClassSpec = 'omiglot-live-data.Mutations.OfScalar.Mutation';
    static Factory = Elevated.factories.concrete<Mutation<any>>(Mutation.ClassSpec, () => new Mutation<any>());
    getFactory = () => Mutation.Factory;
    getGlobalId(): number|string|null { return this.id || null; }
    marshal(visitor: Elevated.Visitor<this>): void {
        visitor.begin(this);
        visitor.primitive(this, 'id');
        visitor.scalar(Status.Factory, 'status');
        visitor.scalar(Revert.Factory, 'revert');
        visitor.scalar(Assign.Factory, 'assign');
        visitor.scalar(Clear.Factory, 'clear') ;
        visitor.reference(Mutation.Factory, 'prev');
        visitor.reference(Mutation.Factory, 'next');
        visitor.end(this);
    }

    isClear(): boolean {
        return !!this.clear;
    }

    isSumma(): boolean {
        return !!this.assign || !!this.clear;
    }

    clone(prev: Elevated.Reference<this>): this {
        const result = new Mutation<ValueType>();
        result.prev = prev;
        result.status = this.status;
        result.assign = this.assign;
        result.clear = this.clear;
        return <this>result;
    }
    toString() {
        if(this.assign) {
            return `ASSIGN ${JSON.stringify(this.assign.value)}`;
        } else if(this.clear) {
            return `CLEAR`;
        } else if(this.clear) {
            return `SYNC`;
        }
        return `?`;
    }

    get(): ValueType|undefined {
        return this.clear ? undefined
            : this.assign ? this.assign.value
            : undefined;
    }

    dispatch(client: ScalarClient<ValueType>) {
        if(this.assign && this.assign.value !== undefined) {
          client.assign(this.assign.value);
        } else if(this.clear) {
          client.clear();
        } else if(this.revert) {
        }
    }
    
    static fromSync<ValueType>(
        prev: Elevated.Reference<Mutation<ValueType>>|Mutation<ValueType>|null,
        status_: Streams.Status
    ) {
        const result = new Mutation<ValueType>();
        result.prev = Elevated.Reference.from(prev);
        result.status = Status.from(status_);
        return result;
    }
    static fromRevert<ValueType>(
        prev: Elevated.Reference<Mutation<ValueType>>|Mutation<ValueType>
    ) {
        const globalId = prev.getGlobalId()
        if(globalId === null) {
            throw new Error('NULL globalId')
        }
        const result = new Mutation<ValueType>();
        result.prev = Elevated.Reference.from(prev);
        result.revert = Revert.from({ toMutation: Elevated.Reference.from(prev) });
        return result;
    }    
    static fromAssign<ValueType>(
        prev: Elevated.Reference<Mutation<ValueType>>|Mutation<ValueType>|null,
        value: ValueType|undefined
    ) {
        const result = new Mutation<ValueType>();
        result.prev = Elevated.Reference.from(prev);
        result.assign = Assign.from({ value });
        return result;
    }
    static fromClear<ValueType>(
        prev: Elevated.Reference<Mutation<ValueType>>|Mutation<ValueType>|null
    ) {
        const result = new Mutation<ValueType>();
        result.prev = Elevated.Reference.from(prev);
        result.clear = Clear.from({ });
        return result;
    }
}

function Optimize<ValueType>(
    mutation: Mutation<ValueType>,
    fetch: (ref: Elevated.Reference<Mutation<ValueType>>) => Promise<Mutation<ValueType>>, 
    insert: (mutation: Mutation<ValueType>) => void, 
    remove: (mutation: Mutation<ValueType>) => void
): Promise<Mutation<ValueType>> {
    if(!!mutation.clear || (!!mutation.assign && mutation.assign.value === undefined)) {
        const replacement = <Mutation<ValueType>>Mutation.fromClear<ValueType>(Elevated.Reference.fromNull())
        insert(replacement);
        return Elevated.Reference.from(mutation).collect((mutation: Mutation<ValueType>) => mutation.prev, fetch, (mutation: Mutation<ValueType>) => false)
        .then(mutations => mutations.forEach((mutation: Mutation<ValueType>) => remove(mutation)))
        .then(() => replacement);
    } else if(!!mutation.assign) {
        const replacement = <Mutation<ValueType>>Mutation.fromAssign<ValueType>(Elevated.Reference.fromNull(), mutation.assign.value)
        insert(replacement);
        return Elevated.Reference.from(mutation).collect((mutation: Mutation<ValueType>) => mutation.prev, fetch, (mutation: Mutation<ValueType>) => false)
        .then(mutations => mutations.forEach((mutation: Mutation<ValueType>) => remove(mutation)))
        .then(() => replacement);
    }
    return Promise.resolve(mutation);
}

export function Tally<
    ValueType, 
    DomainTypeIn extends Elevated.Domain<ValueType>, 
    ResultType,
    DomainTypeOut extends Elevated.Domain<ResultType>
>(
    name: string,
    stream: ScalarStream<ValueType, DomainTypeIn>,
    clear: () => ResultType,
    add: (accum: ResultType, value_: ValueType) => ResultType,
    remove: (accum: ResultType, value_: ValueType) => ResultType,
    domain: DomainTypeOut
) {
    return {
        persistKey: Stores.Key.fromEmpty(),
        peerId: Elevated.factories.makeObjectId(),
        domain,
        watch(peerId: string) { return new class {
            mutations(observer: rxjs.Observer<Mutation<ResultType>>) {
                let _accum = clear();
                let _mutation: null|Mutation<ResultType> = null;
                // operators must prime the observer!
                observer.next(Mutation.fromClear<ResultType>(null).prime());
                return stream.watch(name).mutations(new class {
                    next(received: Mutation<ValueType>) {
                        let found = false;
                        if(received.assign) {
                            _accum = clear();
                            if(received.assign.value !== undefined) {
                                const accum_ = add(_accum, received.assign.value);
                                if(accum_ !== _accum) {
                                    found = true;
                                    _accum = accum_;
                                }
                            }
                        } else if(received.clear) {
                            _accum = clear();
                            found = true;                            
                        }
                        if(found) {
                            _mutation = Mutation.fromAssign(_mutation, _accum);
                            observer.next(_mutation);
                        }
                    }
                    complete() { observer.complete() }
                    error(err:any) { observer.error(err) }
                });
            }
        } },
        persistent: () => undefined,
        stateful: () => undefined,
    }
}

export function Map<ValueType, DomainTypeIn extends Elevated.Domain<ValueType>, ResultType, DomainTypeOut extends Elevated.Domain<ResultType>>(
    name: string,
    stream: ScalarStream<ValueType, DomainTypeIn>,
    transform: (value_: undefined|ValueType) => ResultType,
    context: string,
    domain: DomainTypeOut
): ScalarStream<ResultType, DomainTypeOut> {
    let _mutation: Mutation<ResultType>;
    const transform_ = (mutation: Mutation<ValueType>): Mutation<ResultType> => {
        if(mutation.assign) {
            _mutation = Mutation.fromAssign(_mutation || null, transform(mutation.assign.value));
            _mutation.id = mutation.id;
            return _mutation;
        } else if(mutation.clear) {
            _mutation = Mutation.fromClear(_mutation || null);
            _mutation.id = mutation.id;
            return _mutation;
        } else if(mutation.revert) {
            _mutation = Mutation.fromRevert(_mutation || null);
            _mutation.id = mutation.id;
            return _mutation;
        } else if(mutation.status) {
            _mutation = Mutation.fromSync(_mutation || null, mutation.status.status);
            _mutation.id = mutation.id;
            return _mutation;
        } else {
            throw new Error('Malformed Mutation');
        }
    };
    return {
        persistKey: stream.persistKey,
        peerId: Elevated.factories.makeObjectId(),
        domain, 
        watch(peerId: string) {
            return new class {
                mutations(observer: rxjs.Observer<Mutation<ResultType>>) {
                    // operators must prime the observer!
                    observer.next(Mutation.fromClear<ResultType>(null).prime());
                    return stream.watch(name).mutations({
                        next: mutation_ => observer.next(transform_(mutation_)),
                        complete: () => observer.complete(),
                        error: (err: any) => observer.error(err)
                    });
                }
            }
        },
        persistent: () => undefined,
        stateful: () => undefined
    }
}


export function Eval<
    ArgsType extends {[argName: string]: any }, 
    ScalarStreamsType extends {[argName in keyof ArgsType]: ScalarStream<ArgsType[argName], Elevated.Domain<ArgsType[argName]>>},
    ResultType
>(
    name: string,
    domain: Elevated.Domain<ResultType>,
    function_: (args: ArgsType) => undefined|ResultType,
    streams: ScalarStreamsType
): ScalarStream<ResultType, Elevated.Domain<ResultType>> {
    const keys = Object.getOwnPropertyNames(streams) as (keyof ArgsType)[];
    let _mutation: Mutation<ResultType>;
    const update = (values: any, key: keyof ArgsType, mutation: Mutation<ArgsType[typeof key]>) => {
        let updated = false;
        if(debugMode) {
            console.log(`OfScalar.Eval function: ${function_}`)
            console.log(`OfScalar.Eval values(before): ${JSON.stringify(values)}`)
        }
        if(mutation.assign) {
            updated = values[key] !== mutation.assign.value;
            values[key] = mutation.assign.value;
        } else if(mutation.clear) {
            updated = values[key] !== undefined;
            values[key] = undefined;
        } else if(mutation.revert) {
            updated = values[key] !== undefined;
            values[key] = undefined;
        } else if(mutation.status) {
            updated = values[key] !== undefined;
            values[key] = undefined;
        } else {
            throw new Error('Malformed Mutation');
        }
        if(debugMode) {
            console.log(`OfScalar.Eval values(after): ${JSON.stringify(values)}`)
            console.log(`OfScalar.Eval updated: ${updated}`)
        }
        return updated;
    };

    const makeMutation = (result: undefined|ResultType) => {
        if(result === undefined) {
            _mutation = Mutation.fromClear(_mutation || null);
            return _mutation;
        } else {
            _mutation = Mutation.fromAssign(_mutation || null, result);
            return _mutation;
        }
    };

    return {
        persistKey: Stores.Key.fromEmpty(),
        peerId: Elevated.factories.makeObjectId(),
        domain, 
        watch(peerId: string) { return new class {
            mutations(observer: rxjs.Observer<Mutation<ResultType>>) {
                const values = {} as ArgsType;
                let _result: undefined|ResultType;
                let _remainder: number = keys.length;
                const subscriptions = new Streams.Subscriptions();
                // operators must prime the observer!
                observer.next(Mutation.fromClear<ResultType>(null).prime());
                for(let key of keys) {
                    const stream = streams[key] as ScalarStream<any, Elevated.Domain<any>>;
                    subscriptions.add(stream.watch(name).mutations({
                        next: mutation => {
                            if(update(values, key as keyof ArgsType, mutation) || _result === undefined) {
                                const result_ = function_(values);
                                if(result_ !== _result) {
                                    _result = result_;
                                    const mutation_ = makeMutation(result_);
                                    observer.next(mutation_);
                                }
                            }
                        },
                        complete: () => {
                            --_remainder;
                            if(_remainder === 0)
                                observer.complete()
                        }, error: (err:any) => {
                            observer.error(err)
                        }
                    }));
                }
                return subscriptions;
            }
        }},
        persistent: () => undefined,
        stateful: () => undefined
    }
}

export function Collect<ValueType>(
    name: string,
    combine: (a: undefined|ValueType, b:undefined|ValueType) => undefined|ValueType,
    ...streams: ScalarStream<ValueType, Elevated.Domain<ValueType>>[]
): ScalarStream<ValueType, Elevated.Domain<ValueType>> {
    let _mutation: Mutation<ValueType>;
    let _value: undefined|ValueType;
    let domain: undefined|Elevated.Domain<ValueType>;
    for(let stream of streams) {
        domain = stream.domain;
    }
    if(domain === undefined) {
        throw new Error('Cannot infer domain from streams supplied to Operations.Collect');
    }
    const update = (values: (undefined|ValueType)[], index: number, mutation: Mutation<ValueType>) => {
        if(mutation.assign) {
            values[index] = mutation.assign.value;
        } else if(mutation.clear) {
            values[index] = undefined;
        } else if(mutation.revert) {
            values[index] = undefined;
        } else if(mutation.status) {
            values[index] = undefined;
        } else {
            throw new Error('Malformed Mutation');
        }
    };
    const sense = (values: (undefined|ValueType)[]) => {
        const value_ = values.slice(1).reduce((_accum, value) => combine(_accum, value), values[0]);
        if(_value === value_) {
            return undefined;
        } else if(value_ === undefined) {
            _mutation = Mutation.fromClear(_mutation || null);
            return _mutation;
        } else {
            _mutation = Mutation.fromAssign(_mutation || null, value_);
            return _mutation;
        }
    };

    return {
        persistKey: Stores.Key.fromEmpty(),
        peerId: Elevated.factories.makeObjectId(),
        domain, 
        watch(peerId: string) { return new class {
            mutations(observer: rxjs.Observer<Mutation<ValueType>>) {
                const values: (undefined|ValueType)[] = [];
                const subscriptions = new Streams.Subscriptions();
                // operators must prime the observer!
                observer.next(Mutation.fromClear<ValueType>(null).prime());
                for(let stream of streams) {
                    values.push(undefined);
                }
                streams.forEach((stream, index) => {
                    subscriptions.add(stream.watch(name).mutations({
                        next: mutation => {
                            update(values, index, mutation);
                            const mutation_ = sense(values);
                            if(mutation_) observer.next(mutation_);
                        },
                        complete: () => observer.complete(),
                        error: (err: any) => observer.error(err)
                    }));
                });
                return subscriptions;
            }
        } },
        persistent: () => undefined,
        stateful: () => undefined
    }
}

export function makeLiteral<ValueType>(
    value: ValueType
): ScalarStream<ValueType, Elevated.Domain<ValueType>> {
    const updates = new rxjs.Subject<Mutations.OfScalar.Mutation<ValueType>>();
    const asString = `${value}`;
    const domain = new class extends Elevated.Domain<ValueType> {
        asString(format?: string) { return undefined; }
        asEnumeration(maxCount: number) { return new class {
            *forward(): Generator<ValueType> { yield value; }
            *backward(): Generator<ValueType> { yield value; }
        } }
        cmp(a: ValueType, b:ValueType): undefined|-1|0|1 { return a < b ? -1 : a > b ? +1 : 0 }
    }
    return {
        persistKey: Stores.Key.fromEmpty(),
        peerId: Elevated.factories.makeObjectId(),
        domain,
        watch(peerId: string) { return new class {
            mutations(observer: rxjs.Observer<Mutations.OfScalar.Mutation<ValueType>>) {
                observer.next(Mutations.OfScalar.Mutation.fromAssign(null, value));
                observer.complete();
                return new Streams.Subscriptions();
            }
        } },
        persistent: () => undefined,
        stateful: () => undefined,
    }
}

export function makeClear<ValueType>(
    domain: Elevated.Domain<ValueType>
): ScalarStream<ValueType, Elevated.Domain<ValueType>> {
    const updates = new rxjs.Subject<Mutations.OfScalar.Mutation<ValueType>>();
    return {
        persistKey: Stores.Key.fromEmpty(),
        peerId: Elevated.factories.makeObjectId(),
        domain,
        watch(peerId: string) { return new class {
            mutations(observer: rxjs.Observer<Mutations.OfScalar.Mutation<ValueType>>) {
                observer.next(Mutations.OfScalar.Mutation.fromClear(null));
                observer.complete();
                return new Streams.Subscriptions();
            }
        } },
        persistent: () => undefined,
        stateful: () => undefined,
    }
}

export function makeSubject<ValueType>(
    domain: Elevated.Domain<ValueType>,
    subject: rxjs.BehaviorSubject<ValueType|undefined>
) {
    let _mutation: Mutations.OfScalar.Mutation<ValueType>|undefined; 
    const updates = new rxjs.Subject<Mutations.OfScalar.Mutation<ValueType>>();
    subject.subscribe(value_ => {
        if(value_ === undefined) {
            _mutation = Mutations.OfScalar.Mutation.fromClear(_mutation || null);
            updates.next(_mutation);
        } else {
            _mutation = Mutations.OfScalar.Mutation.fromAssign(_mutation || null, value_);
            updates.next(_mutation);
        }
    })
    return {
        context: "",
        domain,
        watchMe: () => { return {
            catchup: rxjs.of(Mutations.OfScalar.Mutation.fromClear<ValueType>(null)),
            updates
        } },
        persistent: () => undefined,
        stateful: () => undefined,
    }
}
